import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { DynamicForm } from '../dynamic-form/dynamic-form';
import { UserService } from '../services/user/user.service';
import { SubsidiaryService } from '../services/subsidiary/subsidiary.service';
import { ResponsibilityService } from '../services/responsibility/responsibility.service';
import { ToasterService, AuthService } from '@ui-nuvertos/auth';
// Relative imports (NOT '@shared-lib'). This file lives inside libs/shared,
// and @shared-lib is a federation sharedMapping — going through it from
// inside the same lib creates an MF re-entry where DynamicForm resolves to
// undefined in this @Component's imports list and crashes with
// "Cannot read properties of undefined (reading 'ɵcmp')".
import { CenterStore } from '../stores/center.store';
import { environment } from '../environments/environment';
import { lastValueFrom } from 'rxjs';

// Local interfaces for role management (to avoid circular dependency with @ui-nuvertos/settings)
interface Role {
  id: number;
  name: string;
  description: string;
  center_id: number;
  is_system: boolean;
  is_active: boolean;
}

const DefaultRoleNames = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  ACCOUNTANT: 'Accountant',
  VIEWER: 'Viewer'
};

interface FieldConfig {
  id: number;
  template_id: number;
  field_name: string;
  display_name: string;
  field_type: string;
  data_type: string;
  is_mandatory: boolean;
  is_visible: boolean;
  is_custom_field: boolean;
  tab_id: string | number;
  sub_tab_id: number;
  display_order: number;
  validation_rules: string;
  help_text: string;
  default_value: any;
  is_active: boolean;
  is_deleted: boolean;
  width: number;
  options?: any[];
}

interface SubTabConfig {
  id: number;
  title: string;
  sub_type: 'table' | 'field';
  show_heading: boolean;
  is_active: boolean;
  is_deleted: boolean;
  type?: string;
  content: FieldConfig[];
}

interface TabConfig {
  id: number;
  name: string;
  is_active: boolean;
  is_deleted: boolean;
  sub_tabs: SubTabConfig[];
}

interface DynamicFormTab {
  title: string;
  value: number;
  sub_tabs: SubTabConfig[];
}

@Component({
  selector: 'lib-user',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    TabsModule,
    DynamicForm
  ],
  templateUrl: './user.html',
  styleUrls: ['./user.css']
})
export class User implements OnInit {
  userId: string | null = null;
  mode: string = 'create';
  isLoading = false;
  isSubmitting = false;
  userForm!: FormGroup;
  primaryInfoExpanded = true;
  
  data: FieldConfig[] = [];
  primaryFields: FieldConfig[] = [];
  response: any;
  selectedTemplate: any = null;
  loadedTemplateId: string | null = null;
  
  tabData: TabConfig[] = [];
  dynamicFormTabs: DynamicFormTab[] = [];
  activeTabIndex = 2; // Start with tab 2 (Roles) since tab 1 is in accordion
  initializedTabs: Set<number> = new Set();
  
  // Role-related properties
  roles: Role[] = [];
  currentUserRole: string = '';
  isSuperAdmin: boolean = false;

  // Responsibility-related properties.
  // IDs stay as strings end-to-end — snowflake IDs (18-digit) overflow JS
  // Number precision when coerced via Number(), zeroing the trailing digits.
  responsibilities: Array<{ id: string; name: string }> = [];
  private initialResponsibilityIds: string[] = [];

  private http = inject(HttpClient);
  private authUrl = environment.authUrl + '/permission';
  private bmwUrl = (environment as any).bmwUrl || environment.accountingUrl;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private userService: UserService,
    private subsidiaryService: SubsidiaryService,
    private responsibilityService: ResponsibilityService,
    private toasterService: ToasterService,
    private authService: AuthService,
    private centerStore: CenterStore
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    
    // Get mode and userId from route params
    const routeParams = this.route.snapshot.params;
    const currentUrl = this.router.url;
    
    if (routeParams['userId']) {
      this.userId = routeParams['userId'];
      // Determine mode from URL path
      if (currentUrl.includes('/edit/')) {
        this.mode = 'edit';
      } else if (currentUrl.includes('/view/')) {
        this.mode = 'view';
      } else {
        this.mode = 'view'; // default to view if userId is present
      }
    } else {
      this.mode = 'create';
      this.userId = null;
    }

    await this.loadAllData();
    this.isLoading = false;
  }

  async loadAllData() {
    try {
      // Create basic form structure
      this.createBasicFormStructure();
      this.processTemplateData();
      
      // Load subsidiary options, roles, and responsibilities in parallel
      await Promise.all([
        this.loadSubsidiaries(),
        this.loadRoles(),
        this.loadResponsibilities()
      ]);

      // Load user data if editing/viewing
      if (this.userId && (this.mode === 'edit' || this.mode === 'view')) {
        await this.loadUserData();
      } else {
        this.initializeForm();
      }
    } catch (error) {
      console.error('Error loading data:', error);
      this.toasterService.error('Error', 'Failed to load form data');
    }
  }

  private async loadRoles(): Promise<void> {
    try {
      const centerId = this.centerStore.centerId();
      if (!centerId) return;

      // Load current user's role first
      const currentUser = this.authService.getCurrentUser();
      if (currentUser) {
        try {
          const params = new HttpParams()
            .set('user_id', currentUser.id.toString())
            .set('center_id', centerId.toString());
          const roleResponse = await lastValueFrom(
            this.http.get<{ role: string }>(`${this.authUrl}/roles/user-role`, { params })
          );
          this.currentUserRole = roleResponse.role || '';
          this.isSuperAdmin = this.currentUserRole === DefaultRoleNames.SUPER_ADMIN;
        } catch {
          // Current user might not have a role
          this.currentUserRole = '';
          this.isSuperAdmin = false;
        }
      }

      // Load all roles
      const roleParams = new HttpParams().set('center_id', centerId.toString());
      const rolesResponse = await lastValueFrom(
        this.http.get<{ roles: Role[] }>(`${this.authUrl}/roles`, { params: roleParams })
      );
      let allRoles = rolesResponse.roles || [];

      // Filter out Super Admin - it should never be assignable from UI
      allRoles = allRoles.filter(r => r.name !== DefaultRoleNames.SUPER_ADMIN);

      this.roles = allRoles;

      // Update the role field options in data and dynamicFormTabs
      const roleOptions = this.roles.map(role => ({
        label: role.name,
        value: role.name
      }));

      // Update role field in tabData
      this.tabData.forEach(tab => {
        tab.sub_tabs.forEach(subTab => {
          const roleField = subTab.content.find(f => f.field_name === 'role');
          if (roleField) {
            roleField.options = roleOptions;
          }
        });
      });

      // Update role field in data array
      const roleFields = this.data.filter(f => f.field_name === 'role');
      roleFields.forEach(field => {
        field.options = roleOptions;
      });

      // Rebuild dynamic form tabs to pick up new options
      this.buildDynamicFormTabs();
    } catch (error) {
      console.error('Error loading roles:', error);
      // Continue without roles if API call fails
    }
  }
  
  private async loadResponsibilities(): Promise<void> {
    try {
      const res = await lastValueFrom(
        this.responsibilityService.list({ limit: '200', include_inactive: 'false' })
      );
      const items = res?.data?.responsibilities || res?.data || [];
      this.responsibilities = Array.isArray(items)
        ? items.map((r: any) => ({ id: String(r.id), name: r.name }))
        : [];

      const options = this.responsibilities.map((r) => ({ label: r.name, value: r.id }));

      // Inject options into tabData + flat data for field_name 'responsibility_ids'
      this.tabData.forEach((tab) => {
        tab.sub_tabs.forEach((subTab) => {
          const field = subTab.content.find((f) => f.field_name === 'responsibility_ids');
          if (field) field.options = options;
        });
      });
      this.data
        .filter((f) => f.field_name === 'responsibility_ids')
        .forEach((f) => (f.options = options));

      this.buildDynamicFormTabs();
    } catch {
      // Endpoint may not be reachable from every app — silently skip
      this.responsibilities = [];
    }
  }

  private async loadSubsidiaries(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.subsidiaryService.getSubsidiaries({ include_inactive: 'false' })
      );
      const subsidiaries = response?.data?.subsidiaries || response?.data || [];
      
      const subsidiaryOptions = Array.isArray(subsidiaries)
        ? subsidiaries.map((subsidiary: any) => ({
            id: subsidiary.id,
            name: subsidiary.name || subsidiary.subsidiary_name || '',
            value: subsidiary.id,
            label: subsidiary.name || subsidiary.subsidiary_name || '',
            ...subsidiary
          }))
        : [];
      
      // Update subsidiary_id field options in primaryFields
      const subsidiaryField = this.primaryFields.find(f => f.field_name === 'subsidiary_id');
      if (subsidiaryField) {
        subsidiaryField.options = subsidiaryOptions;
      }
      
      // Also update in data array
      const subsidiaryFields = this.data.filter(f => f.field_name === 'subsidiary_id');
      subsidiaryFields.forEach(field => {
        field.options = subsidiaryOptions;
      });
    } catch (error) {
      console.error('Error loading subsidiaries:', error);
      // Continue without subsidiaries if API call fails
    }
  }


  createBasicFormStructure() {
    // Hardcoded tabData structure with tabs, subtabs, and fields
    this.tabData = [
      {
        id: 1,
        name: 'Primary Information',
        is_active: true,
        is_deleted: false,
        sub_tabs: [
          {
            id: 1,
            title: 'User Information',
            sub_type: 'field',
            show_heading: false,
            is_active: true,
            is_deleted: false,
            content: [
              {
                id: 1,
                template_id: 0,
                field_name: 'first_name',
                display_name: 'First Name',
                field_type: 'input',
                data_type: 'string',
                is_mandatory: true,
                is_visible: true,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 1,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1
              },
              {
                id: 2,
                template_id: 0,
                field_name: 'last_name',
                display_name: 'Last Name',
                field_type: 'input',
                data_type: 'string',
                is_mandatory: true,
                is_visible: true,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 2,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1
              },
              {
                id: 3,
                template_id: 0,
                field_name: 'email',
                display_name: 'Email',
                field_type: 'input',
                data_type: 'string',
                is_mandatory: true,
                is_visible: true,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 3,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1
              },
              {
                id: 4,
                template_id: 0,
                field_name: 'phone_number',
                display_name: 'Phone',
                field_type: 'input',
                data_type: 'string',
                is_mandatory: false,
                is_visible: true,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 4,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1
              },
              {
                id: 5,
                template_id: 0,
                field_name: 'password',
                display_name: 'Password',
                field_type: 'password',
                data_type: 'string',
                is_mandatory: false, // Will be set conditionally in initializeForm
                is_visible: true,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 5,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1
              },
              {
                id: 6,
                template_id: 0,
                field_name: 'subsidiary_id',
                display_name: 'Subsidiary',
                field_type: 'select',
                data_type: 'string',
                is_mandatory: false,
                is_visible: false,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 6,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 2,
                options: [
                ]
              }
            ]
          }
        ]
      },
      {
        id: 2,
        name: 'Roles',
        is_active: true,
        is_deleted: false,
        sub_tabs: [
          {
            id: 1,
            title: 'Role Information',
            sub_type: 'field',
            show_heading: false,
            is_active: true,
            is_deleted: false,
            content: [
              {
                id: 4,
                template_id: 0,
                field_name: 'role',
                display_name: 'Role',
                field_type: 'select',
                data_type: 'string',
                is_mandatory: false,
                is_visible: true,
                is_custom_field: false,
                tab_id: 2,
                sub_tab_id: 1,
                display_order: 1,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1,
                options: [
                  { label: 'Admin', value: 'admin' },
                  { label: 'User', value: 'user' },
                  { label: 'Manager', value: 'manager' }
                ]
              },
              {
                id: 5,
                template_id: 0,
                field_name: 'responsibility_ids',
                display_name: 'Responsibilities',
                field_type: 'multiselect',
                data_type: 'array',
                is_mandatory: false,
                is_visible: true,
                is_custom_field: false,
                tab_id: 2,
                sub_tab_id: 1,
                display_order: 2,
                validation_rules: '',
                help_text: '',
                default_value: [],
                is_active: true,
                is_deleted: false,
                width: 1,
                options: []
              },
              // {
              //   id: 5,
              //   template_id: 0,
              //   field_name: 'department',
              //   display_name: 'Department',
              //   field_type: 'select',
              //   data_type: 'string',
              //   is_mandatory: false,
              //   is_visible: true,
              //   is_custom_field: false,
              //   tab_id: 2,
              //   sub_tab_id: 1,
              //   display_order: 2,
              //   validation_rules: '',
              //   help_text: '',
              //   default_value: '',
              //   is_active: true,
              //   is_deleted: false,
              //   width: 1,
              //   options: [
              //     { label: 'IT', value: 'it' },
              //     { label: 'HR', value: 'hr' },
              //     { label: 'Finance', value: 'finance' },
              //     { label: 'Operations', value: 'operations' }
              //   ]
              // }
            ]
          }
        ]
      }
    ];
    
    // Extract all fields from tabData for backward compatibility
    this.data = [];
    this.tabData.forEach(tab => {
      tab.sub_tabs.forEach(subTab => {
        this.data.push(...subTab.content);
      });
    });
    
    // Build dynamicFormTabs from tabData
    this.buildDynamicFormTabs();
  }
  
  
  private buildDynamicFormTabs(): void {
    // Filter out tab 1 (Primary Information) - it will be shown in accordion
    // Only include tabs with id > 1 (like Roles)
    const tabsToShow = this.tabData.filter((tab: TabConfig) => tab.id > 1);
    
    this.dynamicFormTabs = tabsToShow.map((tab: TabConfig) => ({
      title: this.capitalize(tab.name),
      value: tab.id,
      sub_tabs: (tab.sub_tabs ?? []).map((subTab: SubTabConfig) => ({
        ...subTab,
        title: subTab.title,
        sub_type: subTab.sub_type ?? 'field',
        type: subTab.type ?? subTab.sub_type,
        content: this.data.filter(f => f.tab_id == tab.id && f.sub_tab_id == subTab.id)
      }))
    }));
    
    if (this.dynamicFormTabs.length > 0) {
      const firstTabValue = this.dynamicFormTabs[0]?.value;
      if (!this.initializedTabs.has(this.activeTabIndex) ||
          !this.dynamicFormTabs.find(t => t.value === this.activeTabIndex)) {
        this.activeTabIndex = Number(firstTabValue);
      }
      
      // Don't initialize tab data here - wait for form to be created
      // initializeTabData will be called from initializeForm()
    }
  }
  
  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
  
  private initializeTabData(tabValue: number): void {
    if (this.initializedTabs.has(tabValue)) {
      return;
    }
    
    // Ensure userForm exists before trying to use it
    if (!this.userForm) {
      return;
    }
    
    const tab = this.dynamicFormTabs.find(t => t.value == tabValue);
    if (!tab) {
      return;
    }
    
    // Initialize form controls for this tab
    tab.sub_tabs.forEach(subTab => {
      subTab.content.forEach(field => {
        const fieldKey = this.getFieldKey(field.field_name);
        if (!this.userForm.get(fieldKey)) {
          // For multiselect fields, default_value should be an array
          // For select fields, default_value should be a string
          const value = field.field_type === 'multiselect' 
            ? (field.default_value || [])
            : (field.default_value || '');
          const validators = field.is_mandatory ? [Validators.required] : [];
          this.userForm.addControl(fieldKey, this.fb.control(value, validators));
        }
      });
    });
    
    this.initializedTabs.add(tabValue);
  }
  
  onTabChange(tabValue: any): void {
    this.activeTabIndex = tabValue;
    if (!this.initializedTabs.has(tabValue)) {
      this.initializeTabData(tabValue);
    }
  }

  processTemplateData() {
    // For now, we're using hardcoded tabData structure
    // This method can be used later if we need to load from API
    if (this.response?.data) {
      // If template data is provided, we can merge it with hardcoded structure
      // For now, just use the hardcoded structure
    }
    
    // Filter primary fields (tab_id = 1) for backward compatibility
    this.primaryFields = this.data
      .filter((field: FieldConfig) => field.tab_id === 1 && field.is_visible && !field.is_deleted)
      .sort((a: FieldConfig, b: FieldConfig) => a.display_order - b.display_order);
  }

  initializeForm(userData?: any) {
    const formControls: any = {};
    
    // Split username into first_name and last_name if editing
    if (userData?.username) {
      const nameParts = userData.username.trim().split(/\s+/);
      userData.first_name = nameParts[0] || '';
      userData.last_name = nameParts.slice(1).join(' ') || '';
    }
    
    // Initialize form controls from primary fields (tab 1)
    this.primaryFields.forEach((field: FieldConfig) => {
      const fieldKey = this.getFieldKey(field.field_name);
      // Don't populate password field when editing (for security)
      let value = '';
      if (field.field_name === 'password') {
        value = ''; // Always empty for password field
      } else {
        value = userData?.[field.field_name] || field.default_value || '';
      }
      // Password is required only on create mode
      const validators = field.is_mandatory || (field.field_name === 'password' && this.mode === 'create') 
        ? [Validators.required] 
        : [];
      
      formControls[fieldKey] = [value, validators];
    });
    
    // Initialize form controls from all tabs (excluding tab 1 which is in primaryFields)
    this.dynamicFormTabs.forEach(tab => {
      tab.sub_tabs.forEach(subTab => {
        subTab.content.forEach((field: FieldConfig) => {
          const fieldKey = this.getFieldKey(field.field_name);
          // For multiselect fields, default_value should be an array
          // For select fields, default_value should be a string
          const defaultValue = field.field_type === 'multiselect' 
            ? (field.default_value || [])
            : (field.default_value || '');
          const value = userData?.[field.field_name] || defaultValue;
          const validators = field.is_mandatory ? [Validators.required] : [];
          
          formControls[fieldKey] = [value, validators];
        });
      });
    });
    
    this.userForm = this.fb.group(formControls);
    
    // Initialize first tab if tabs exist
    if (this.dynamicFormTabs.length > 0) {
      // Ensure activeTabIndex is valid
      if (!this.dynamicFormTabs.find(t => t.value === this.activeTabIndex)) {
        this.activeTabIndex = this.dynamicFormTabs[0]?.value || 2;
      }
      this.initializeTabData(this.activeTabIndex);
    }
    
    if (this.mode === 'view') {
      this.userForm.disable();
    }
  }

  async loadUserData() {
    if (!this.userId) return;

    try {
      const response = await lastValueFrom(this.userService.getUserById(this.userId));
      const userData = response?.data || response;
      if (userData) {
        // Extract role from centers array (first center's role)
        if (userData.centers && userData.centers.length > 0) {
          userData.role = userData.centers[0].role;
        }

        // Load user's currently assigned responsibilities
        try {
          const respRes = await lastValueFrom(
            this.responsibilityService.getForUser(this.userId)
          );
          const items = respRes?.data?.responsibilities || respRes?.data || [];
          const ids = Array.isArray(items) ? items.map((r: any) => String(r.id)) : [];
          this.initialResponsibilityIds = ids;
          userData.responsibility_ids = ids;
        } catch {
          this.initialResponsibilityIds = [];
        }

        this.initializeForm(userData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      this.toasterService.error('Error', 'Failed to load user data');
    }
  }

  getFieldKey(fieldName: string): string {
    return fieldName;
  }

  onFormFieldChanged(event: { key: string; value: any }) {
    // Handle field changes if needed
  }

  async onSubmit() {
    if (this.userForm.invalid) {
      this.toasterService.error('Error', 'Please fill all required fields');
      return;
    }

    this.isSubmitting = true;
    
    try {
      const formValue = this.userForm.value;
      const payload: any = {};
      
      // Merge first_name and last_name into username
      const firstName = formValue['first_name'] || '';
      const lastName = formValue['last_name'] || '';
      payload.username = `${firstName} ${lastName}`.trim();
      
      // Map form values back to API format from primary fields
      this.primaryFields.forEach((field: FieldConfig) => {
        const fieldKey = this.getFieldKey(field.field_name);
        const value = formValue[fieldKey];
        
        // Skip first_name and last_name as they're merged into username
        if (field.field_name === 'first_name' || field.field_name === 'last_name') {
          return;
        }
        
        // Handle password - only include if it has a value (for updates, empty means don't change)
        if (field.field_name === 'password') {
          if (this.mode === 'create') {
            // Password is required on create
            payload.password = value;
          } else if (this.mode === 'edit' && value && value.trim() !== '') {
            // Only include password on update if it's provided
            payload.password = value;
          }
          return;
        }
        
        // Handle subsidiary_id separately - it will be sent as selected value
        if (field.field_name === 'subsidiary_id') {
          payload.subsidiary_id = value;
        } else {
          payload[field.field_name] = value;
        }
      });
      
      // Always send center_id as 1 in the payload
      payload.center_id = 1;
      
      // Map form values back to API format from all tabs (excluding tab 1)
      // responsibility_ids is included in the payload AND synced separately via the
      // user-responsibility endpoint (safety net if backend doesn't consume the
      // array inline yet).
      this.dynamicFormTabs.forEach(tab => {
        tab.sub_tabs.forEach(subTab => {
          subTab.content.forEach((field: FieldConfig) => {
            const fieldKey = this.getFieldKey(field.field_name);
            payload[field.field_name] = formValue[fieldKey];
          });
        });
      });

      const selectedRespIds: string[] = Array.isArray(formValue['responsibility_ids'])
        ? formValue['responsibility_ids']
            .map((v: any) => (v == null ? '' : String(v)).trim())
            .filter((s: string) => s !== '')
        : [];

      let savedUserId: string | number | null = null;

      if (this.mode === 'create') {
        const createRes = await lastValueFrom(this.userService.createUser(payload));
        savedUserId = createRes?.data?.id || createRes?.id || null;
        this.toasterService.success('Success', 'User created successfully');
      } else if (this.mode === 'edit' && this.userId) {
        payload.id = Number(this.userId);
        await lastValueFrom(this.userService.updateUser(payload));
        savedUserId = this.userId;
        this.toasterService.success('Success', 'User updated successfully');
      }

      if (savedUserId) {
        await this.syncUserResponsibilities(savedUserId, selectedRespIds);
      }

      this.router.navigate(['/settings/account_settings/users']);
    } catch (error: any) {
      console.error('Error saving user:', error);
      this.toasterService.error('Error', error?.error?.message || 'Failed to save user');
    } finally {
      this.isSubmitting = false;
    }
  }

  onCancel() {
    this.router.navigate(['/settings/account_settings/users']);
  }

  private async syncUserResponsibilities(userId: string | number, selectedIds: string[]): Promise<void> {
    const currentSet = new Set(this.initialResponsibilityIds);
    const nextSet = new Set(selectedIds);

    const toAdd = selectedIds.filter((id) => !currentSet.has(id));
    const toRemove = this.initialResponsibilityIds.filter((id) => !nextSet.has(id));

    try {
      if (toAdd.length > 0) {
        await lastValueFrom(this.responsibilityService.assignToUser(userId, toAdd));
      }
      for (const rid of toRemove) {
        await lastValueFrom(this.responsibilityService.removeFromUser(userId, rid));
      }
    } catch {
      // Non-fatal — user record is saved. Warn but don't block navigation.
      this.toasterService.warn('Warning', 'Could not fully sync responsibilities');
    }
  }
}

